/**
 * Analytics configuration (config only — no scripts injected).
 *
 * Reads optional VITE_* env vars and exposes a typed config object.
 * Analytics providers stay fully disabled until BOTH:
 *   1. The env var is set at build time, AND
 *   2. A provider loader is wired up in `src/routes/__root.tsx`
 *      (intentionally deferred until production deployment).
 *
 * Supported env vars:
 *   VITE_GA_ID              — Google Analytics 4 measurement ID (G-XXXXXXX)
 *   VITE_GSC_VERIFICATION   — Google Search Console meta-tag content value
 *   VITE_CLARITY_ID         — Microsoft Clarity project ID
 *   VITE_POSTHOG_API_KEY    — PostHog project API key
 *   VITE_POSTHOG_HOST       — PostHog host (defaults to https://us.i.posthog.com)
 */

const env = (name: string): string | undefined => {
  const value = (import.meta.env as Record<string, string | undefined>)[name];
  return value && value.trim() ? value.trim() : undefined;
};

export type AnalyticsConfig = {
  googleAnalyticsId?: string;
  googleSearchConsoleVerification?: string;
  clarityId?: string;
  posthog?: { key: string; host: string };
};

export const analyticsConfig: AnalyticsConfig = {
  googleAnalyticsId: env("VITE_GA_ID"),
  googleSearchConsoleVerification: env("VITE_GSC_VERIFICATION"),
  clarityId: env("VITE_CLARITY_ID"),
  posthog: env("VITE_POSTHOG_API_KEY") || env("VITE_POSTHOG_KEY")
    ? {
        key: (env("VITE_POSTHOG_API_KEY") || env("VITE_POSTHOG_KEY"))!,
        // Region must match the project the key belongs to. Configured via
        // VITE_POSTHOG_HOST; falls back to EU (current project region).
        host: env("VITE_POSTHOG_HOST") ?? "https://eu.i.posthog.com",
      }
    : undefined,
};

/** True when at least one analytics provider is configured via env. */
export const analyticsEnabled: boolean = Boolean(
  analyticsConfig.googleAnalyticsId ||
    analyticsConfig.clarityId ||
    analyticsConfig.posthog,
);